<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ $t('bo.resources.createTitle') }}</h2>
        <button @click="$emit('close')" class="btn-close">&times;</button>
      </div>

      <div class="modal-body">
        <div v-if="error" class="error-banner">{{ error }}</div>

        <div class="form-group">
          <label>{{ $t('bo.resources.labelTitle') }} <span class="required">*</span></label>
          <input v-model="form.title" type="text" class="input" maxlength="150" :placeholder="$t('bo.resources.titlePlaceholder')" />
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>{{ $t('bo.resources.colType') }} <span class="required">*</span></label>
            <select v-model="form.type" class="input">
              <option value="">{{ $t('bo.resources.chooseType') }}</option>
              <option v-for="tp in TYPES" :key="tp.value" :value="tp.value">{{ tp.label }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>{{ $t('bo.resources.colAudience') }}</label>
            <select v-model="form.target_audience" class="input">
              <option value="">{{ $t('bo.resources.audienceBoth') }}</option>
              <option value="mentors">{{ $t('bo.resources.audienceMentors') }}</option>
              <option value="mentees">{{ $t('bo.resources.audienceMentees') }}</option>
              <option value="both">{{ $t('bo.resources.audienceBoth') }}</option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label>{{ $t('bo.resources.labelAvailability') }}</label>
          <select v-model="form.availability" class="input">
            <option value="generic">{{ $t('bo.resources.availabilityGeneric') }}</option>
            <option value="program_specific">{{ $t('bo.resources.availabilityProgramSpecific') }}</option>
          </select>
          <span class="hint">{{ $t('bo.resources.availabilityHint') }}</span>
        </div>

        <div class="form-group">
          <label>{{ $t('bo.resources.labelLink') }} <span class="required">*</span></label>
          <input v-model="form.link" type="url" class="input" maxlength="2000" placeholder="https://..." />
        </div>

        <div class="form-group">
          <label>{{ $t('bo.common.description') }}</label>
          <textarea v-model="form.description" class="textarea" rows="3" maxlength="2000" :placeholder="$t('bo.resources.descriptionPlaceholder')" />
        </div>

        <div class="form-group">
          <label>{{ $t('bo.resources.labelCoverImage') }}</label>
          <CoverImageInput v-model="form.cover_image_url" />
        </div>
      </div>

      <div class="modal-footer">
        <button @click="$emit('close')" class="btn-cancel">{{ $t('bo.common.cancel') }}</button>
        <button @click="submit" :disabled="loading || !canSubmit" class="btn-submit">
          {{ loading ? $t('bo.common.creating') : $t('bo.resources.createResource') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import adminResourceService from '@/services/adminResourceService'
import CoverImageInput from '@/components/ui/CoverImageInput.vue'

const { t } = useI18n()

const emit = defineEmits(['close', 'created'])

const TYPES = computed(() => [
  { value: 'article', label: t('bo.resources.types.article') },
  { value: 'video', label: t('bo.resources.types.video') },
  { value: 'document', label: t('bo.resources.types.document') },
  { value: 'template', label: t('bo.resources.types.template') },
  { value: 'tool', label: t('bo.resources.types.tool') },
  { value: 'slides', label: t('bo.resources.types.slides') },
  { value: 'link', label: t('bo.resources.types.link') },
  { value: 'course', label: t('bo.resources.types.course') },
  { value: 'other', label: t('bo.resources.types.other') },
])

const loading = ref(false)
const error = ref('')
const form = ref({
  title: '',
  type: '',
  link: '',
  description: '',
  cover_image_url: '',
  target_audience: '',
  availability: 'generic',
})

const canSubmit = computed(() => form.value.title.trim() && form.value.type && form.value.link.trim())

const submit = async () => {
  if (!canSubmit.value) return
  loading.value = true
  error.value = ''
  try {
    const payload = { ...form.value }
    if (!payload.description) delete payload.description
    if (!payload.cover_image_url) delete payload.cover_image_url
    if (!payload.target_audience) delete payload.target_audience
    await adminResourceService.create(payload)
    emit('created')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.resources.failedCreate')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  line-height: 1;
  padding: 0 0.25rem;
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.error-banner {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.required { color: #dc2626; }

.hint {
  font-size: 0.75rem;
  color: #6b7280;
}

.form-row {
  display: flex;
  gap: 1rem;
}

.input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  width: 100%;
  box-sizing: border-box;
  background: white;
}

.input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}

.textarea {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  resize: vertical;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}

.textarea:focus {
  outline: none;
  border-color: #3b82f6;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #374151;
}

.btn-cancel:hover { background: #f9fafb; }

.btn-submit {
  padding: 0.5rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
}

.btn-submit:hover:not(:disabled) { background: #2563eb; }
.btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
